import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Copy, Download, ExternalLink, Code2, Layers } from "lucide-react";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  atomOneDark,
  atomOneLight,
} from "react-syntax-highlighter/dist/esm/styles/hljs";
import { useTheme } from "../context/ThemeContext";
import toast from "react-hot-toast";

const TEMPLATES = {
  "1": {
    title: "Animated 3D Forms",
    description:
      "Animated 3D Forms is a modern, interactive login and signup page with a futuristic 3D design. It features smooth transitions, stylish input fields, and a glowing aesthetic, creating a unique user experience. Fully responsive, it adapts seamlessly across all devices. 🚀✨",
    content: `// 3D Forms Example Code
<div class="container">
  <div class="forms-wrapper">
    <form class="signin-form">
      <a href="#" class="signup-btn">Sign Up</a>
      <h2><img src="images/RXS Logo Circle.png" alt="RXS Logo" class="logo">Sign In</h2>
      <div class="inputs-wrapper">
        <input type="text" placeholder="Your Email" />
        <input type="password" placeholder="Password" />
        <input type="submit" value="Sign In" />
      </div>
    </form>
    <form class="signup-form">
      <a href="#" class="signin-btn">Sign In</a>
      <h2><img src="images/RXS Logo Circle.png" alt="RXS Logo" class="logo">Sign Up</h2>
      <div class="inputs-wrapper">
        <input type="text" placeholder="Your Name" />
        <input type="text" placeholder="Your Email" />
        <input type="password" placeholder="Password" />
        <input type="password" placeholder="Confirm Password" />
        <input type="submit" value="Sign Up" />
      </div>
    </form>
  </div>
</div>`,
    previewUrl: "https://3d-rxstech.vercel.app/",
    sourceCodeUrl: "https://link-center.net/1295181/3d-forms-source-code",
    thumbnailUrl: "https://i.imgur.com/jb84FT6.png",
    category: "Portfolio",
    technologies: ["HTML", "CSS", "JavaScript"],
    features: [
      "Responsive design",
      "3D animated transitions",
      "Stylish input fields with unique borders",
      "Glowing neon aesthetic",
      "Smooth form-switching effect",
      "Interactive buttons with hover effects",
    ],
    created_at: "2024-02-15",
  },
};

export default function CodeView() {
  const { id } = useParams();
  const { theme } = useTheme();
  const template = TEMPLATES[id as keyof typeof TEMPLATES];
  const [activeTab, setActiveTab] = useState<"preview" | "code">("preview");

  if (!template) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <h2 className="text-2xl font-bold dark:text-white">
          Template not found
        </h2>
      </div>
    );
  }

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(template.content);
    toast.success("Code copied to clipboard!");
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2 dark:text-white">
              {template.title}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-4 max-w-2xl">
              {template.description}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {template.technologies.map((tech) => (
                <span
                  key={tech}
                  className="px-2 py-1 text-sm rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={template.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Live Preview
            </a>
            <a
              href={template.sourceCodeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Source Code
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="mb-6 flex gap-4 border-b dark:border-gray-700">
              <button
                onClick={() => setActiveTab("preview")}
                className={`px-4 py-2 border-b-2 transition-colors ${
                  activeTab === "preview"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 dark:text-gray-300"
                }`}
              >
                <Layers className="inline-block h-4 w-4 mr-2" />
                Preview
              </button>
              <button
                onClick={() => setActiveTab("code")}
                className={`px-4 py-2 border-b-2 transition-colors ${
                  activeTab === "code"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 dark:text-gray-300"
                }`}
              >
                <Code2 className="inline-block h-4 w-4 mr-2" />
                Source Code
              </button>
            </div>

            {activeTab === "preview" ? (
              <div className="aspect-video rounded-lg overflow-hidden border dark:border-gray-700">
                <img
                  src={template.thumbnailUrl}
                  alt={template.title}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="relative">
                <button
                  onClick={copyToClipboard}
                  className="absolute right-4 top-4 p-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600"
                  title="Copy code"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <SyntaxHighlighter
                  language="typescript"
                  style={theme === "dark" ? atomOneDark : atomOneLight}
                  customStyle={{
                    padding: "1.5rem",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                  }}
                >
                  {template.content}
                </SyntaxHighlighter>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-4 dark:text-white">
              Features
            </h3>
            <ul className="space-y-2">
              {template.features.map((feature, index) => (
                <li
                  key={index}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-300"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
